import { generateAccessName, AccessKey } from "../../../../../../../access";

/**
 * Base keys of the access sections on this page.
 */
const ACCESS_SECTION: AccessKey[] = [AccessKey.ORDERS, AccessKey.ORDER_MODAL];

/**
 * Enum representing special keys for each tab on this page.
 * These keys are used to uniquely identify and manage individual tabs.
 */
enum TabKeys {
	CLOSED_ORDER = AccessKey.CLOSED_ORDER,
	COPY_ORDER = AccessKey.COPY_ORDER,
	SMS_BUTTON = AccessKey.SMS_BUTTON,
	CALL_ORDER_CLIENT = AccessKey.CALL_ORDER_CLIENT,
	CALL_ORDER_EXECUTOR = AccessKey.CALL_ORDER_EXECUTOR,
	SHOW_CLIENT_NUMBER_TO_EXECUTOR = AccessKey.SHOW_CLIENT_NUMBER_TO_EXECUTOR,
	//* Btns filter
	STREET = AccessKey.STREET,
	LOCAL_OBJECT = AccessKey.LOCAL_OBJECT,
	OBJECT = AccessKey.OBJECT,
	//* Btns main
	CLOSE_ALL = AccessKey.CLOSE_ALL,
	CANCEL = AccessKey.CANCEL,
	SAVE = AccessKey.SAVE,
	ADD_NEW_ORDER = AccessKey.ADD_NEW_ORDER,
	//* Btns statistic
	OUT_OF_TOWN_DISTANCE = AccessKey.OUT_OF_TOWN_DISTANCE,
	IDLE_TIME = AccessKey.IDLE_TIME,
	PRICE = AccessKey.PRICE,
	ADDITIONAL_PRICE = AccessKey.ADDITIONAL_PRICE,
	AWAITING_TIME = AccessKey.AWAITING_TIME,
	HOURLY_TIME = AccessKey.HOURLY_TIME,
	//* Btns map
	ROUTE_MAP = AccessKey.ROUTE_MAP,
	PRICE_ZONES_MAP = AccessKey.PRICE_ZONES_MAP,
	SECTORS_MAP = AccessKey.SECTORS_MAP,
	PARKINGS_MAP = AccessKey.PARKINGS_MAP,
	DRIVER_MAP = AccessKey.DRIVER_MAP,
	CLIENT_MAP = AccessKey.CLIENT_MAP,
	CENTER_MAP = AccessKey.CENTER_MAP,
	//* Tabs
	MAP = AccessKey.MAP,
	EVENTS = AccessKey.EVENTS,
	CONVERSATIONS = AccessKey.CONVERSATIONS,
	//* checkBoxs OrderRideInformation
	AUTO_SUPPLY = AccessKey.AUTO_SUPPLY,
	OUT_OF_TOWN = AccessKey.OUT_OF_TOWN,
	SUPPLY = AccessKey.SUPPLY,
	HOURLY = AccessKey.HOURLY,
	TAXIMETER = AccessKey.TAXIMETER,
	DISCOUNT_PLAN = AccessKey.DISCOUNT_PLAN,
	BONUS_TRIP = AccessKey.BONUS_TRIP,
	BONUS_BALANCE = AccessKey.BONUS_BALANCE,
	ACCUMULATIVE_DISCOUNT = AccessKey.ACCUMULATIVE_DISCOUNT,
	MAX_BONUSES_AND_INPUT = AccessKey.MAX_BONUSES_AND_INPUT,
	//* selects head
	TRANSPORTATION_TYPE = AccessKey.TRANSPORTATION_TYPE,
	COMPANIES_AND_TAXI_SERVICES = AccessKey.COMPANIES_AND_TAXI_SERVICES,
	//* selects OrderInformation
	ORDER_TIME = AccessKey.ORDER_TIME,
	PAYMENT_TYPE = AccessKey.PAYMENT_TYPE,
	TARIFF = AccessKey.TARIFF,
	PASSENGER_COUNT = AccessKey.PASSENGER_COUNT,
	CAR_CLASSES = AccessKey.CAR_CLASSES,
	SERVICES = AccessKey.SERVICES,
	//* field
	PERSONAL_INFORMATION = AccessKey.PERSONAL_INFORMATION,
	POINT_TABLE = AccessKey.POINT_TABLE,
	//* permissions
	VIEW = AccessKey.VIEW,
	EDIT = AccessKey.EDIT,
}

/**
 * Constructs an array of access addresses by combining base access addresses
 * with the tab-specific access keys. This builds the full set of addresses
 * needed to manage access from the page level to the specific tab.
 *
 * @param key - Tab-specific access keys.
 * @returns Array of access addresses including base and tab-specific addresses.
 */
const generateAccessSection = (key: AccessKey): AccessKey[] => [
	...ACCESS_SECTION,
	key,
];

/**
 * Maps each tab on the page to its corresponding array of access addresses.
 * This container is used for managing access control not only for the tabs themselves,
 * but also for internal elements such as buttons within these tabs.
 * Each entry provides a set of access addresses specific to the tab.
 */
const ARR_ACCESS_PART_KEY: Record<TabKeys, AccessKey[]> = {
	[TabKeys.CLOSED_ORDER]: generateAccessSection(AccessKey.CLOSED_ORDER),
	[TabKeys.COPY_ORDER]: generateAccessSection(AccessKey.COPY_ORDER),
	[TabKeys.SMS_BUTTON]: generateAccessSection(AccessKey.SMS_BUTTON),
	[TabKeys.CALL_ORDER_CLIENT]: generateAccessSection(
		AccessKey.CALL_ORDER_CLIENT,
	),
	[TabKeys.CALL_ORDER_EXECUTOR]: generateAccessSection(
		AccessKey.CALL_ORDER_EXECUTOR,
	),
	[TabKeys.SHOW_CLIENT_NUMBER_TO_EXECUTOR]: generateAccessSection(
		AccessKey.SHOW_CLIENT_NUMBER_TO_EXECUTOR,
	),
	//* Btns filter
	[TabKeys.STREET]: generateAccessSection(AccessKey.STREET),
	[TabKeys.LOCAL_OBJECT]: generateAccessSection(AccessKey.LOCAL_OBJECT),
	[TabKeys.OBJECT]: generateAccessSection(AccessKey.OBJECT),
	//* Btns main
	[TabKeys.CLOSE_ALL]: generateAccessSection(AccessKey.CLOSE_ALL),
	[TabKeys.CANCEL]: generateAccessSection(AccessKey.CANCEL),
	[TabKeys.SAVE]: generateAccessSection(AccessKey.SAVE),
	[TabKeys.ADD_NEW_ORDER]: generateAccessSection(AccessKey.ADD_NEW_ORDER),
	//* Btns statistic
	[TabKeys.OUT_OF_TOWN_DISTANCE]: generateAccessSection(
		AccessKey.OUT_OF_TOWN_DISTANCE,
	),
	[TabKeys.IDLE_TIME]: generateAccessSection(AccessKey.IDLE_TIME),
	[TabKeys.PRICE]: generateAccessSection(AccessKey.PRICE),
	[TabKeys.ADDITIONAL_PRICE]: generateAccessSection(
		AccessKey.ADDITIONAL_PRICE,
	),
	[TabKeys.AWAITING_TIME]: generateAccessSection(AccessKey.AWAITING_TIME),
	[TabKeys.HOURLY_TIME]: generateAccessSection(AccessKey.HOURLY_TIME),
	//* Btns map
	[TabKeys.ROUTE_MAP]: generateAccessSection(AccessKey.ROUTE_MAP),
	[TabKeys.PRICE_ZONES_MAP]: generateAccessSection(AccessKey.PRICE_ZONES_MAP),
	[TabKeys.SECTORS_MAP]: generateAccessSection(AccessKey.SECTORS_MAP),
	[TabKeys.PARKINGS_MAP]: generateAccessSection(AccessKey.PARKINGS_MAP),
	[TabKeys.DRIVER_MAP]: generateAccessSection(AccessKey.DRIVER_MAP),
	[TabKeys.CLIENT_MAP]: generateAccessSection(AccessKey.CLIENT_MAP),
	[TabKeys.CENTER_MAP]: generateAccessSection(AccessKey.CENTER_MAP),
	//* Tabs
	[TabKeys.MAP]: generateAccessSection(AccessKey.MAP),
	[TabKeys.EVENTS]: generateAccessSection(AccessKey.EVENTS),
	[TabKeys.CONVERSATIONS]: generateAccessSection(AccessKey.CONVERSATIONS),
	//* checkBoxs OrderRideInformation
	[TabKeys.AUTO_SUPPLY]: generateAccessSection(AccessKey.AUTO_SUPPLY),
	[TabKeys.OUT_OF_TOWN]: generateAccessSection(AccessKey.OUT_OF_TOWN),
	[TabKeys.SUPPLY]: generateAccessSection(AccessKey.SUPPLY),
	[TabKeys.HOURLY]: generateAccessSection(AccessKey.HOURLY),
	[TabKeys.TAXIMETER]: generateAccessSection(AccessKey.TAXIMETER),
	[TabKeys.DISCOUNT_PLAN]: generateAccessSection(AccessKey.DISCOUNT_PLAN),
	[TabKeys.BONUS_TRIP]: generateAccessSection(AccessKey.BONUS_TRIP),
	[TabKeys.BONUS_BALANCE]: generateAccessSection(AccessKey.BONUS_BALANCE),
	[TabKeys.ACCUMULATIVE_DISCOUNT]: generateAccessSection(
		AccessKey.ACCUMULATIVE_DISCOUNT,
	),
	[TabKeys.MAX_BONUSES_AND_INPUT]: generateAccessSection(
		AccessKey.MAX_BONUSES_AND_INPUT,
	),
	//* selects OrderInformation
	[TabKeys.ORDER_TIME]: generateAccessSection(AccessKey.ORDER_TIME),
	[TabKeys.PAYMENT_TYPE]: generateAccessSection(AccessKey.PAYMENT_TYPE),
	[TabKeys.TARIFF]: generateAccessSection(AccessKey.TARIFF),
	[TabKeys.PASSENGER_COUNT]: generateAccessSection(AccessKey.PASSENGER_COUNT),
	[TabKeys.CAR_CLASSES]: generateAccessSection(AccessKey.CAR_CLASSES),
	[TabKeys.SERVICES]: generateAccessSection(AccessKey.SERVICES),
	//* selects head
	[TabKeys.TRANSPORTATION_TYPE]: generateAccessSection(
		AccessKey.TRANSPORTATION_TYPE,
	),
	[TabKeys.COMPANIES_AND_TAXI_SERVICES]: generateAccessSection(
		AccessKey.COMPANIES_AND_TAXI_SERVICES,
	),
	//* field
	[TabKeys.PERSONAL_INFORMATION]: generateAccessSection(
		AccessKey.PERSONAL_INFORMATION,
	),
	[TabKeys.POINT_TABLE]: generateAccessSection(AccessKey.POINT_TABLE),
	//* permissions
	[TabKeys.VIEW]: generateAccessSection(AccessKey.VIEW),
	[TabKeys.EDIT]: generateAccessSection(AccessKey.EDIT),
};

/**
 * Type representing the access names for each tab.
 */
type TabAccessNamesType = {
	[key in TabKeys]: string;
};

/**
 * Generates access names from arrays of access keys for each tab.
 * This mapping creates a unique access name for each tab, which is used
 * to determine whether the tab is accessible or not. Each access name represents
 * the combined access keys for the tab.
 *
 * @returns TabAccessNamesType - An object mapping each tab key to its unique access name
 */
const TabAccessNames: TabAccessNamesType = Object.fromEntries(
	Object.entries(ARR_ACCESS_PART_KEY).map(([key, section]) => [
		key,
		generateAccessName(...section),
	]),
) as TabAccessNamesType;

export { TabKeys, TabAccessNames, ARR_ACCESS_PART_KEY };
